import { useMemo, useEffect, useState } from 'react';
import { useRouter } from '@blocs.router';
import { QUERY_PARAMS, SESSION_COUPON_CODE, UTM_SOURCE } from '../constants';

const SESSION_COUPON_TYPE = 'session';

const codeKey: string = QUERY_PARAMS.couponCode;
const couponTypeKey: string = QUERY_PARAMS.couponType;
const planTermKey: string = QUERY_PARAMS.planTerm;
const deeplinkKey: string = QUERY_PARAMS.deeplink;
const fbclidKey: string = QUERY_PARAMS.fbclid;

const useCouponCodeCatcher = () => {
  const { search, pathname, replace } = useRouter();
  const [complete, setComplete] = useState<boolean>(false);
  const queryParams = useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);
  const couponCode = queryParams.get(codeKey);
  const planTerm = queryParams.get(planTermKey);
  const deeplink = queryParams.get(deeplinkKey);
  const couponType = queryParams.get(couponTypeKey);
  const utmSource = queryParams.get(UTM_SOURCE);
  const fbclid = queryParams.get(fbclidKey);
  const localStorageCode = localStorage.getItem(codeKey);
  const localStoragePlanTerm = localStorage.getItem(planTermKey);
  const localStorageDeeplink = localStorage.getItem(deeplinkKey);
  const sessionUtmSource = sessionStorage.getItem(UTM_SOURCE);
  const sessionFbclid = sessionStorage.getItem(fbclidKey);

  // if there is a coupon code on the query string, persist into local storage
  // so that it will be present the next time the user completes
  // an validated authentication
  useEffect(() => {
    const hasCouponCode = couponCode && localStorageCode !== couponCode;
    const hasPlanTerm = planTerm && localStoragePlanTerm !== planTerm;
    const hasDeeplink = deeplink && localStorageDeeplink !== deeplink;
    const hasUtmSource = utmSource && sessionUtmSource !== utmSource;
    const hasFbclid = fbclid && sessionFbclid !== fbclid;

    if (!complete && (hasCouponCode || hasPlanTerm || hasDeeplink || hasUtmSource || hasFbclid)) {
      if (hasCouponCode) {
        // if the coupon code redemption is session based, set the session-based boolean in local storage
        if (couponType === SESSION_COUPON_TYPE) {
          localStorage.setItem(SESSION_COUPON_CODE, couponCode);
          queryParams.delete(couponTypeKey);
        } else {
          // set coupon code into localStorage
          localStorage.setItem(codeKey, couponCode);
        }
        // remove coupon code from the query params
        queryParams.delete(codeKey);
      }

      if (hasPlanTerm) {
        // set plan term into local storage
        localStorage.setItem(planTermKey, planTerm);
        // remove plan term from the query params
        queryParams.delete(planTermKey);
      }

      if (hasDeeplink) {
        // set deeplink into local storage
        localStorage.setItem(deeplinkKey, deeplink);
        // remove deeplink from the query params
        queryParams.delete(deeplinkKey);
      }

      if (hasUtmSource) {
        // set utm source into session storage so it can be detected in the registration flow
        // after the user creates an account or logs in
        // this will pick up if the user tries to come directly to the open call link but isn't logged in
        // before they are redirected to the login/registration page(s)
        sessionStorage.setItem(UTM_SOURCE, utmSource);
        // do not remove utm source because it needs to be passed to the login/registration page
        // if the user is not logged in
      }

      if (hasFbclid) {
        // set the fbClickId into session storage, to be used in either to send it with purchaseSubscription mutation
        sessionStorage.setItem(fbclidKey, fbclid);
      }

      replace({ to: `${pathname}${queryParams.toString() ? `?${queryParams.toString()}` : ''}` });
    }

    if (!complete) {
      setComplete(true);
    }
  }, [
    complete,
    couponCode,
    couponType,
    deeplink,
    fbclid,
    replace,
    pathname,
    localStorageCode,
    localStorageDeeplink,
    localStoragePlanTerm,
    planTerm,
    queryParams,
    sessionFbclid,
    sessionUtmSource,
    utmSource,
  ]);

  return { complete };
};

export default useCouponCodeCatcher;
